/**
 * The admin session, as React state.
 *
 * FR-8.1b is the whole reason this file exists: **admin accounts authenticate
 * against the same user store with `admin` role**, and a member's credentials
 * must not unlock this app. That is enforced server-side by
 * `deps.current_admin` (403 `forbidden` for a member token, 401 for anything
 * else) and by `authenticate_admin` refusing a non-admin at login. This provider
 * only holds the result - there is no client-side role check anywhere, because a
 * client-side check would be decoration rather than a control.
 *
 * The session itself lives in `lib/session.ts` (memory + `sessionStorage`) and
 * the transport in `lib/api/client.ts`; this is the React view of both.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { configureSessionLossHandler, restoreSession } from "./api/client";
import { login as loginRequest, logout as logoutRequest } from "./api/auth";
import { session, type AdminIdentity } from "./session";

export type AuthStatus = "restoring" | "signed-out" | "signed-in";

type AuthValue = {
  status: AuthStatus;
  admin: AdminIdentity | null;
  /**
   * Set when the session ended without the admin asking - an expired refresh
   * token, or a 403 that says the session is not an admin's. The login screen
   * shows it, so "why am I back here" always has an answer.
   */
  signedOutReason: string | null;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("restoring");
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);

  const endSession = useCallback((reason: string | null) => {
    setAdmin(null);
    setSignedOutReason(reason);
    setStatus("signed-out");
  }, []);

  // The transport tells us when a refresh has definitively failed. Without this
  // the app would keep rendering signed-in chrome over requests that all 401.
  useEffect(() => {
    configureSessionLossHandler(() => {
      endSession("Your session has ended. Please log in again.");
    });
    return () => configureSessionLossHandler(null);
  }, [endSession]);

  /**
   * Restore on mount.
   *
   * A reload leaves the refresh token in `sessionStorage` and nothing else - the
   * access token and the identity are memory-only by design - so this performs
   * one rotation and gets both back from the refresh response.
   *
   * `restoring` is a distinct status rather than a flavour of `signed-out`
   * because the alternative is a visible flash of the login screen on every
   * reload for an admin who is already signed in.
   */
  useEffect(() => {
    let active = true;
    if (!session.getRefreshToken()) {
      setStatus("signed-out");
      return;
    }
    restoreSession()
      .then((identity) => {
        if (!active) return;
        if (identity) {
          setAdmin(identity);
          setStatus("signed-in");
        } else {
          endSession("Your session has ended. Please log in again.");
        }
      })
      .catch(() => {
        if (active) endSession("Your session has ended. Please log in again.");
      });
    return () => {
      active = false;
    };
  }, [endSession]);

  const signIn = useCallback(async (identifier: string, password: string) => {
    // A member's credentials fail here with the server's generic
    // `invalid_credentials` sentence, which is FR-8.1b: the admin app never
    // learns that the identifier belongs to a member, and the member cannot use
    // it to learn that an admin account exists.
    const identity = await loginRequest(identifier, password);
    setSignedOutReason(null);
    setAdmin(identity);
    setStatus("signed-in");
  }, []);

  const signOut = useCallback(async () => {
    await logoutRequest();
    setAdmin(null);
    setSignedOutReason(null);
    setStatus("signed-out");
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, admin, signedOutReason, signIn, signOut }),
    [status, admin, signedOutReason, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>.");
  return value;
}
